/**
 * SettingsIntegrations — verbatim port of SettingsIntegrations (F-84–F-87).
 * Payments · Calendars · Video conferencing · Email · SMS
 * List/Cards view · search · category chips · per-provider detail config.
 */
import { useState, useEffect } from "react";
import { alpha, Btn, Card, Icon, Input, Pill, T, Toggle } from "@v0y4ge/jrni-ui";
import { ProviderLogo } from "./ProviderLogo";
import { PageHeader } from "../../shell/chrome";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";
import type { IntegrationDetail as CatalogDetail } from "@v0y4ge/api-contracts";
import { listIntegrationDetails, disconnectIntegration, verifyIntegration } from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import SettingsShell from "./SettingsShell";
import { getPayPalConnection, type PayPalConnection } from "./paypal-connect-api";
import { getAdyenConnectionStatus, type AdyenConnection } from "./adyen-connect-api";
import { getStripeStatus, type StripeStatusResponse } from "./stripe-connect-api";
import { backendProviderId, refreshConnections, type IntegStatusKind } from "./integration-configs";
import { useViewMode, ViewToggle, CARD_GRID } from "../../shell/list-composites";
import { FilterBar } from "../../shell/composites";
import { useT, t as i18nT } from "../../lib/i18n";

/* ── Integration catalog ─────────────────────────────────────────────── */
interface IntegProvider {
  id: string; name: string; group: string; category: string;
  brand: string; icon: string; desc: string; auth: string;
  registrationId?: string; featureKey?: string;
  inputs?: { l: string; placeholder: string; type?: string; filled?: string }[];
}

const PROVIDER_PRESENTATION: IntegProvider[] = [ // ui-config
  // Payments
  { id: "stripe",      name: "Stripe",           group: "payment",  category: "Payments",        brand: "#635BFF", icon: "S",  desc: "Card payments, Apple/Google Pay, PaymentIntents — the industry standard for event ticketing.",     auth: "OAuth 2.0",     inputs: [{ l: "Publishable key", placeholder: "pk_live_…" }] },
  // ui-config
  { id: "square",      name: "Square",            group: "payment",  category: "Payments",        brand: "#000000", icon: "□",  desc: "POS + online payments, ideal for walk-up ticket sales and mixed in-person/online events.",          auth: "OAuth 2.0",     inputs: [{ l: "Application ID", placeholder: "sq0idp-…" }] },
  { id: "paypal",      name: "PayPal",            group: "payment",  category: "Payments",        brand: "#003087", icon: "P",  desc: "PayPal Checkout and Braintree — supports 200+ markets and 25+ currencies.",                        auth: "REST API",       inputs: [{ l: "Client ID", placeholder: "AV…" }] },
  { id: "anet",        name: "Authorize.net",     group: "payment",  category: "Payments",        brand: "#E0792B", icon: "A",  desc: "Card payments and eChecks through the Authorize.net gateway, with hosted Accept forms and refunds.", auth: "API login ID" },
  { id: "adyen",       name: "Adyen",             group: "payment",  category: "Payments",        brand: "#0ABF53", icon: "A",  desc: "Enterprise processing with 100+ local payment methods and unified global settlement.",              auth: "API key" },
  { id: "braintree",   name: "Braintree",         group: "payment",  category: "Payments",        brand: "#000000", icon: "B",  desc: "PayPal-owned gateway for cards and digital wallets with built-in fraud protection.",                auth: "API credentials" },
  { id: "razorpay",    name: "Razorpay",          group: "payment",  category: "Payments",        brand: "#0C2451", icon: "R",  desc: "Accept UPI, cards and netbanking for events across India and APAC markets.",                        auth: "Key ID + secret" },
  // Calendars
  { id: "google-cal",  name: "Google Calendar",   group: "calendar", category: "Calendars",       brand: "#4285F4", icon: "G",  desc: "Two-way sync — push events to staff calendars and import busy/free blocks to prevent double-booking.", auth: "OAuth 2.0" },
  { id: "outlook",     name: "Microsoft 365 Calendar", group: "calendar", category: "Calendars",       brand: "#0078D4", icon: "O",  desc: "Microsoft 365 Calendar two-way sync via Microsoft Graph.",                                          auth: "OAuth 2.0",  inputs: [{ l: "Tenant ID", placeholder: "Azure AD directory ID" }] },
  { id: "exchange",    name: "Microsoft Exchange", group: "calendar", category: "Calendars",       brand: "#0F6CBD", icon: "E",  desc: "On-premise Exchange Server via EWS — for organisations not on Exchange Online.",                       auth: "Basic / NTLM",inputs: [{ l: "EWS URL", placeholder: "https://mail.company.com/EWS/Exchange.asmx" }] },
  { id: "entra",      name: "Microsoft Entra ID", group: "identity", category: "Access management", brand: "#0078D4", icon: "E",  desc: "Integrate Microsoft Entra ID for Office 365 calendar synchronization. This integration allows you to automatically sync appointments with your Office 365 calendar and manage them efficiently.", auth: "OAuth 2.0 · Azure AD", inputs: [{ l: "Tenant ID", placeholder: "Azure AD directory ID" }] },
  { id: "icloud",      name: "Apple iCloud",       group: "calendar", category: "Calendars",       brand: "#3693F3", icon: "",  desc: "Two-way sync with iCloud Calendar so events stay current in Apple Calendar on Mac, iPhone and iPad.", auth: "CalDAV" },
  { id: "caldav",      name: "CalDAV",             group: "calendar", category: "Calendars",       brand: "#5B6472", icon: "C",  desc: "Two-way sync with any CalDAV-compatible calendar via a standards-based feed.",                        auth: "Basic auth" },
  { id: "google-directory", name: "Google Directory / SSO", group: "identity", category: "Access management", brand: "#4285F4", icon: "G", desc: "Staff sign-in (SSO) and automatic directory sync from Google Workspace — part of the shared Google app registration.", auth: "OAuth 2.0 · Google" },
  // Video
  { id: "zoom",        name: "Zoom",              group: "video",    category: "Video conferencing",brand: "#2D8CFF", icon: "Z", desc: "Auto-generate Zoom meetings when a virtual event is created; embed join links in confirmations.",     auth: "OAuth 2.0" },
  { id: "teams",       name: "Microsoft Teams",   group: "video",    category: "Video conferencing",brand: "#6264A7", icon: "T", desc: "Create Teams meetings automatically for virtual bookings; join links in all comms.",                  auth: "OAuth 2.0",  inputs: [{ l: "Tenant ID", placeholder: "Azure AD directory ID" }] },
  { id: "webex",       name: "Cisco Webex",       group: "video",    category: "Video conferencing",brand: "#00B140", icon: "W", desc: "Webex meetings for enterprise video conferencing; supports webinars and large virtual events.",       auth: "OAuth 2.0" },
  { id: "googlemeet",  name: "Google Meet",       group: "video",    category: "Video conferencing",brand: "#00897B", icon: "M", desc: "Generate Google Meet links for virtual sessions; attendees join directly from their confirmation.",   auth: "OAuth 2.0" },
  { id: "goto",        name: "GoTo Meeting",      group: "video",    category: "Video conferencing",brand: "#F68D2E", icon: "G", desc: "Generate GoTo Meeting links per event and share them with confirmed guests automatically.",           auth: "OAuth 2.0" },
  // Email
  { id: "sendgrid",    name: "SendGrid",          group: "email",    category: "Email",           brand: "#1A82E2", icon: "SG", desc: "Transactional and marketing email at scale — confirmation, reminder, and campaign delivery.",         auth: "API key",    inputs: [{ l: "API key", placeholder: "SG.…" }] },
  { id: "resend",      name: "Resend",            group: "email",    category: "Email",           brand: "#000000", icon: "R",  desc: "Developer-first transactional & marketing email with a modern API and React Email templates.",         auth: "API key",    inputs: [{ l: "API key", placeholder: "re_…" }] },
  // SMS
  { id: "twilio",      name: "Twilio",            group: "sms",      category: "SMS",             brand: "#F22F46", icon: "~",  desc: "SMS confirmations, day-of reminders, and two-way messaging via Twilio Programmable Messaging.",        auth: "API key",    inputs: [{ l: "Account SID", placeholder: "AC…" }, { l: "Auth Token", placeholder: "Auth token" }] },
  // CRM
  { id: "salesforce",  name: "Salesforce",        group: "crm",      category: "CRM",             brand: "#00A1E0", icon: "SF", desc: "Bi-directional guest sync — push registrations to Salesforce contacts and pull lead data into guest profiles.", auth: "OAuth 2.0" },
  { id: "hubspot",     name: "HubSpot",           group: "crm",      category: "CRM",             brand: "#FF7A59", icon: "H",  desc: "Sync guests as HubSpot contacts; trigger workflows on registration, check-in, and no-show.",          auth: "OAuth 2.0" },
  { id: "dynamics",    name: "Microsoft Dynamics", group: "crm",      category: "CRM",             brand: "#0078D4", icon: "D",  desc: "Two-way sync of contacts, leads, accounts, and event activity with Dynamics 365 Sales.",              auth: "OAuth 2.0" },
  // Analytics
  { id: "ga",          name: "Google Analytics",  group: "analytics",category: "Analytics",        brand: "#E37400", icon: "G",  desc: "Send registration and check-in events to GA4 so landing-page conversions appear in your attribution.", auth: "Measurement Protocol" },
  // Discovery & maps
  { id: "rwg",         name: "Reserve with Google",group: "discovery",category: "Discovery",       brand: "#4285F4", icon: "R",  desc: "List bookable events and appointments on Google Search and Maps; reservations sync back in real time.", auth: "OAuth 2.0" },
  { id: "gmaps",       name: "Google Maps",       group: "maps",     category: "Maps",             brand: "#34A853", icon: "G",  desc: "Render location maps, place details and directions on landing pages, emails, and the check-in kiosk.",  auth: "API key" },
  { id: "amaps",       name: "Apple Maps",        group: "maps",     category: "Maps",             brand: "#34C759", icon: "",  desc: "MapKit JS maps and directions — the most native experience for guests on iPhone, iPad and Mac.",       auth: "MapKit JS token" },
  // Wallet & passes — the shared Apple Developer registration (pass signing).
  { id: "apple",       name: "Apple",             group: "wallet",   category: "Wallet & passes",  brand: "#000000", icon: "", desc: "Apple Wallet passes for tickets, appointments and queue positions — signed with your Pass Type ID certificate.", auth: "Pass Type ID certificate" },
  // Automation
  { id: "zapier",      name: "Zapier",            group: "automation",category: "Automation",      brand: "#FF4A00", icon: "Z",  desc: "Connect to 5,000+ apps via Zaps — trigger on registration, check-in, capacity hit, and waitlist moves.", auth: "API key" },
  // Slack
  { id: "slack",       name: "Slack",             group: "alerts",   category: "Alerts",          brand: "#4A154B", icon: "#",  desc: "Pipe event alerts (capacity hit, no-shows, sync failures) into a Slack channel.",                       auth: "OAuth 2.0" },
  // Student information systems — the read-only Ethos lane into Banner. A category of
  // its own (backend `category`/`group`: "sis"): it is neither identity nor CRM, and a
  // college's operator looks for it by name (CD brief 2026-08-19-0002, kit 7cc9e00e).
  { id: "ethos",       name: "Ellucian Ethos (Banner SIS)", group: "sis", category: "Student information", brand: "#6244BB", icon: "E", desc: "Read-only student-information lane into Banner \u2014 resolves the signed-in student at SSO login, then reads academic level, program and holds so gating and pricing can use real student status. Never writes to Banner.", auth: "API key", inputs: [{ l: "Ethos Integration base URL", placeholder: "https://integrate.elluciancloud.com" }, { l: "API key", placeholder: "Issued with the Ethos application", type: "password" }] },
  // Product imports (placeholder — populated later)
  { id: "product-import", name: "Product catalog",   group: "products", category: "Product imports",   brand: "#5142C7", icon: "P", desc: "Import an external product catalog to attach products to events and services.",                          auth: "" },
];

const CATEGORY_LABELS: Record<string, string> = { // ui-config
  payment: "Payments", calendar: "Calendars", identity: "Access management", sis: "Student information",
  video: "Video conferencing", email: "Email", sms: "SMS", crm: "CRM", analytics: "Analytics",
  discovery: "Discovery", maps: "Maps", wallet: "Wallet & passes", automation: "Automation", alerts: "Alerts", products: "Product imports",
};

function catalogProvider(row: CatalogDetail): IntegProvider {
  const presentation = PROVIDER_PRESENTATION.find((p) => p.id === row.uiId || p.id === row.provider || backendProviderId(p.id) === row.provider);
  const group = row.group || row.category || presentation?.group || "other";
  return {
    id: presentation?.id || row.uiId || row.provider,
    name: row.name, desc: row.desc, auth: row.auth,
    group, category: presentation?.category || CATEGORY_LABELS[group] || row.category || "Other",
    brand: row.brand || presentation?.brand || "#5142C7", icon: row.icon || presentation?.icon || row.name.slice(0, 1),
    registrationId: row.registrationId, featureKey: row.featureKey,
  };
}

/* The chip VALUE is the filter key (matched against provider.category) and never
 * translates; only the label does. camelCase leaf per the catalog convention —
 * keys filed in CLAUDE-CODE-REQUESTS, so t() returns the byte-exact fallback
 * until they ship. */
const CATEGORY_KEY: Record<string, string> = {
  "All": "all", "Payments": "payments", "Calendars": "calendars", "Access management": "accessManagement",
  "Student information": "studentInformation",
  "Video conferencing": "videoConferencing", "Email": "email", "SMS": "sms", "CRM": "crm",
  "Analytics": "analytics", "Discovery": "discovery", "Maps": "maps", "Wallet & passes": "walletPasses",
  "Automation": "automation", "Alerts": "alerts", "Product imports": "productImports",
};

/* ── Brand icon ───────────────────────────────────────────────────────── */
function BrandIcon({ p, size = 40 }: { p: IntegProvider; size?: number }) {
  return <ProviderLogo id={p.id} name={p.name} brand={p.brand} glyph={p.icon} size={size} />;
}

/* ── Detail modal ─────────────────────────────────────────────────────── */
/* Detail modal retired — rows now open the full IntegrationDetail page. */

/* ═══ Main screen ════════════════════════════════════════════════════════ */
/* ── §6 honest status pill (kit e670c958) ─────────────────────── */
function StatusPill({ kind }: { kind: IntegStatusKind }) {
  const t = useT();
  if (kind === "connected") return <Pill tone="success" dot>{t("settings.integrations.connected", "Connected")}</Pill>;
  if (kind === "configured-unverified") return <Pill tone="warn" dot>{t("settings.integrations.configuredNotVerified", "Configured · not verified")}</Pill>;
  if (kind === "needs-attention") return <Pill tone="warn">{t("settings.integrations.needsAttention", "Needs attention")}</Pill>;
  if (kind === "error") return <Pill tone="danger">{t("settings.integrations.error", "Error")}</Pill>;
  return <Pill tone="neutral">{t("settings.integrations.notConnected", "Not connected")}</Pill>;
}

export default function SettingsIntegrations() {
  const t = useT();
  const canWrite = can("integrations.write");
  const nav = useNav();
  const [view, setView] = useViewMode("integrations:view");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [catalog, setCatalog] = useState<CatalogDetail[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [catalogRetry, setCatalogRetry] = useState(0);
  useEffect(() => {
    let generation = 0;
    let disposed = false;
    const clear = () => { generation += 1; setCatalog([]); setCatalogError(false); setCatalogLoading(true); };
    const load = async () => {
      clear();
      const request = generation;
      try {
        const all: CatalogDetail[] = [];
        for (let page = 1; ; page += 1) {
          const response = await listIntegrationDetails({ page, limit: 100 });
          if (disposed || request !== generation) return;
          all.push(...response.data);
          if (!response.hasMore) break;
          if (!response.data.length) throw new Error("Incomplete integration catalog");
        }
        setCatalog(all);
      } catch {
        if (!disposed && request === generation) { setCatalog([]); setCatalogError(true); }
      } finally {
        if (!disposed && request === generation) setCatalogLoading(false);
      }
    };
    const offSwitching = onTenantSwitching(() => { clear(); setCategory("All"); });
    const offChange = onTenantChange(() => { void load(); });
    void load();
    return () => { disposed = true; generation += 1; offSwitching(); offChange(); };
  }, [catalogRetry]);
  const providers = catalog.map(catalogProvider);
  const categories = ["All", ...new Set(providers.map((p) => p.category))];
  const hasPayPal = providers.some((p) => p.id === "paypal");
  const hasAdyen = providers.some((p) => p.id === "adyen");
  const hasStripe = providers.some((p) => p.id === "stripe");

  // REQ-045e (merged 2026-07-23): PayPal connected-ness has its OWN source of
  // truth — getPayPalConnection() (the connect-based onboarding ops), not the
  // generic listIntegrationDetails cache. Fetch on mount so the list row
  // reflects the connect flow the detail screen drives; poll while awaiting
  // the receivability webhook so the row flips to Connected live.
  const [ppConn, setPpConn] = useState<PayPalConnection | null>(null);
  useEffect(() => {
    setPpConn(null);
    if (!hasPayPal) return;
    let alive = true;
    let timer: number | undefined;
    const load = async () => {
      try {
        const c = await getPayPalConnection();
        if (!alive) return;
        setPpConn(c);
        if (c.status === "connected_unconfirmed") timer = window.setTimeout(() => { void load(); }, 3000);
      } catch (e) { console.warn("getPayPalConnection unavailable:", e); }
    };
    void load();
    return () => { alive = false; if (timer != null) window.clearTimeout(timer); };
  }, [hasPayPal, catalog]);
  // REQ-060: Adyen mirrors the pattern — its own source of truth is
  // getAdyenConnectionStatus(); poll while "pending" (Adyen KYC in progress)
  // so the row flips to Connected live once verification lands.
  const [adConn, setAdConn] = useState<AdyenConnection | null>(null);
  useEffect(() => {
    setAdConn(null);
    if (!hasAdyen) return;
    let alive = true;
    let timer: number | undefined;
    const load = async () => {
      try {
        const c = await getAdyenConnectionStatus();
        if (!alive) return;
        setAdConn(c);
        if (c.status === "pending") timer = window.setTimeout(() => { void load(); }, 3000);
      } catch (e) { console.warn("getAdyenConnectionStatus unavailable:", e); }
    };
    void load();
    return () => { alive = false; if (timer != null) window.clearTimeout(timer); };
  }, [hasAdyen, catalog]);
  // Payments work-order 2026-07-24: Stripe mirrors the pattern — its own
  // source of truth is getStripeStatus() (REQ-064 connect ops), not the generic
  // cache; poll while "needs-attention" so the row flips live once onboarding
  // completes. Thrown fetches degrade to the cached kind (list rows are
  // non-blocking; the detail page owns the explicit reach-error state).
  const [stConn, setStConn] = useState<StripeStatusResponse | null>(null);
  useEffect(() => {
    setStConn(null);
    if (!hasStripe) return;
    let alive = true;
    let timer: number | undefined;
    const load = async () => {
      try {
        const c = await getStripeStatus();
        if (!alive) return;
        setStConn(c);
        if (c?.status === "needs-attention") timer = window.setTimeout(() => { void load(); }, 3000);
      } catch (e) { console.warn("getStripeStatus unavailable:", e); }
    };
    void load();
    return () => { alive = false; if (timer != null) window.clearTimeout(timer); };
  }, [hasStripe, catalog]);
  const statusKind = (id: string): IntegStatusKind => {
    if (id === "paypal") {
      if (!ppConn || ppConn.status === "not_connected") return "disconnected";
      return ppConn.status === "verified" ? "connected" : "configured-unverified";
    }
    if (id === "adyen") {
      if (!adConn || adConn.status === "not_connected") return "disconnected";
      return adConn.status === "verified" ? "connected" : "configured-unverified";
    }
    if (id === "stripe" && stConn?.connection) {
      return stConn.status === "connected" ? "connected" : "configured-unverified";
    }
    const row = catalog.find((entry) => entry.provider === backendProviderId(id) || entry.uiId === id || entry.provider === id);
    if (!row) return "disconnected";
    const configurationStatus = row.configuration?.status ?? row.configuration_status;
    if (configurationStatus === "error") return "error";
    if (row.status === "disabled" || row.status === "disconnected" || configurationStatus === "disabled") return "disconnected";
    if (row.status === "connected") return "connected";
    if (configurationStatus === "configured") return "configured-unverified";
    return "needs-attention";
  };
  // Shared-registration deep-link (CD brief 2026-07-24, kit 735eab65): a card
  // whose backend catalog row carries registrationId opens the ONE shared
  // app-registration page, with the tapped service's featureKey pre-focused.
  const openDetail = (p: IntegProvider) => {
    if (p.registrationId) { nav.go("settings.integ.detail", { id: p.registrationId, feature: p.featureKey }); return; }
    nav.go("settings.integ.detail", { id: p.id });
  };

  // §6/§7 (kit e670c958): list actions call the REAL integration ops — success
  // feedback only after the awaited call resolves. Connect is an OAuth redirect
  // flow owned by the detail page, so "Connect" here only navigates. ui-local
  const [busyOp, setBusyOp] = useState<string | null>(null);
  const doDisconnect = async (p: IntegProvider) => {
    if (busyOp) return;
    // Payments work-order Task 1: Disconnect → confirm, then success toast.
    if ((p.id === "paypal" || p.id === "adyen" || p.id === "stripe") && !window.confirm(t("settings.integrationDetail.disconnectPaymentConfirm", "Disconnect this payment account? Guests won't be able to check out with it until you reconnect."))) return;
    setBusyOp(p.id);
    try {
      if (p.id === "stripe") {
        // REQ-064: Stripe disconnects via the OAuth-namespaced connect op.
        const { disconnectStripe } = await import("./stripe-connect-api");
        await disconnectStripe();
        setStConn(await getStripeStatus().catch(() => null));
        toast({ tone: "success", title: t("settings.integrations.stripeDisconnected", "Stripe disconnected") });
        return;
      }
      if (p.id === "paypal") {
        // REQ-045e: PayPal disconnects via its own connect-flow op.
        const { disconnectPayPal } = await import("./paypal-connect-api");
        await disconnectPayPal();
        setPpConn(await getPayPalConnection());
        toast({ tone: "success", title: t("settings.integrations.paypalDisconnected", "PayPal disconnected") });
        return;
      }
      if (p.id === "adyen") {
        // REQ-060: Adyen disconnects via its own connect-flow op.
        const { disconnectAdyen } = await import("./adyen-connect-api");
        await disconnectAdyen();
        setAdConn(await getAdyenConnectionStatus());
        toast({ tone: "success", title: t("settings.integrations.adyenDisconnected", "Adyen disconnected") });
        return;
      }
      await disconnectIntegration(backendProviderId(p.id));
      await refreshConnections();
      setCatalogRetry((value) => value + 1);
      toast({ tone: "success", title: i18nT("settings.integrations.pNameDisconnected.icu", { value0: p.name }) });
    } catch (e) {
      toast({ tone: "warn", title: i18nT("settings.integrations.couldnTDisconnectPName.icu", { value0: p.name }), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setBusyOp(null); }
  };
  const doVerify = async (p: IntegProvider) => {
    if (busyOp) return; setBusyOp(p.id);
    try {
      if (p.id === "stripe" && stConn?.connection) {
        // REQ-064: "Verify" for Stripe = the real POST /verify via the broker.
        const { verifyStripe } = await import("./stripe-connect-api");
        const r0 = await verifyStripe();
        setStConn(await getStripeStatus().catch(() => null));
        if (r0.ok) toast({ tone: "success", title: t("settings.integrations.stripeConnectionHealthy", "Stripe connection healthy") });
        else if (r0.error?.code === "ONBOARDING_INCOMPLETE") toast({ tone: "warn", title: t("settings.integrationDetail.stripeNeedsAttentionTitle", "Connected — needs attention"), subtitle: t("settings.integrationDetail.stripeFinishOnboardingMsg", "Finish Stripe onboarding to enable charges.") });
        else toast({ tone: "warn", title: `Stripe: ${r0.error?.message ?? t("settings.integrationDetail.stripeVerificationFailed", "verification failed")}` });
        return;
      }
      if (p.id === "paypal") {
        // REQ-045e: "Verify" for PayPal = re-fetch the connection status.
        const c = await getPayPalConnection();
        setPpConn(c);
        if (c.status === "verified") toast({ tone: "success", title: t("settings.integrations.paypalConnectedVerified", "PayPal connected & verified") });
        else toast({ tone: "warn", title: t("settings.integrations.paypalStillAwaitingConfirmation", "Still awaiting PayPal confirmation"), subtitle: t("settings.integrations.paypalConfirmsReceivabilityViaWebhook", "PayPal confirms receivability via webhook shortly after onboarding.") });
        return;
      }
      if (p.id === "adyen") {
        // REQ-060: "Verify" for Adyen = re-fetch the connection status.
        const c = await getAdyenConnectionStatus();
        setAdConn(c);
        if (c.status === "verified") toast({ tone: "success", title: t("settings.integrations.adyenConnectedVerified", "Adyen connected & verified") });
        else toast({ tone: "warn", title: t("settings.integrations.adyenVerificationStillPending", "Adyen verification still pending"), subtitle: t("settings.integrations.adyenCompletesKycAfterOnboarding", "Adyen completes KYC/verification shortly after onboarding.") });
        return;
      }
      const r = await verifyIntegration(backendProviderId(p.id));
      await refreshConnections();
      setCatalogRetry((value) => value + 1);
      if (r.data.ok) toast({ tone: "success", title: i18nT("settings.integrations.pNameVerified.icu", { value0: p.name }) });
      else toast({ tone: "warn", title: i18nT("settings.integrations.pNameVerificationFailed.icu", { value0: p.name }), subtitle: r.data.error?.message || "The provider rejected the stored credentials." });
    } catch (e) {
      toast({ tone: "warn", title: i18nT("settings.integrations.couldnTVerifyPName.icu", { value0: p.name }), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setBusyOp(null); }
  };

  const rows = providers.filter((p) => {
    if (category !== "All" && p.category !== category) return false;
    if (query.trim() && !`${p.name} ${p.category} ${p.desc}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const connected = providers.filter((p) => statusKind(p.id) === "connected").length;

  const thStyle = { padding: `${T.space[3]} ${T.space[4]}`, textAlign: "left" as const, fontWeight: 600, fontSize: T.text.label.fontSize, textTransform: "uppercase" as const, color: T.soft, whiteSpace: "nowrap" as const };
  const tdStyle = { padding: `${T.space[4]} ${T.space[4]}`, verticalAlign: "middle" as const };

  return (
    <SettingsShell activeTab="integ">
      <PageHeader
        title={t("settings.integrations.integrations", "Integrations")}
        subtitle={i18nT("settings.integrations.connectedOfProvidersLengthConnected.icu", { value0: connected, value1: providers.length })}
        actions={<ViewToggle mode={view} onChange={setView} />}
      />
      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("settings.integrations.searchIntegrations", "Search integrations…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 260 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: `0 ${T.space[1]}` }} />
        {categories.map((cat) => (
          /* ui-local */
          <Btn key={cat} size="sm" onClick={() => setCategory(cat)} style={{ borderRadius: T.radius.pill, fontSize: T.text.bodySm.fontSize, fontWeight: 500, background: category === cat ? T.p1 : T.white, color: category === cat ? T.white : T.n1, border: `1px solid ${category === cat ? T.p1 : T.borderStrong}`, cursor: "pointer", fontFamily: T.font, whiteSpace: "nowrap", opacity: 1 }}>{t("settings.integrations.categories." + (CATEGORY_KEY[cat] ?? cat), cat)}</Btn>
        ))}
        <span style={{ marginLeft: "auto", fontSize: T.text.label.fontSize, color: T.soft }}>{rows.length} {t("settings.integrations.integrationeb92cc52", "integration")}{" "}{rows.length === 1 ? "" : t('settings.integrations.text13')}</span>
      </FilterBar>

      <div style={{ flex: 1, overflow: "auto", padding: `0 ${T.space[6]} ${T.space[6]}` }}>
        {catalogLoading ? (
          <div role="status" style={{ padding: 32, color: T.soft }}>{t("settings.integrations.loadingCatalog", "Loading integrations…")}</div>
        ) : catalogError ? (
          <div role="alert" style={{ padding: 32, color: T.soft }}>
            <p>{t("settings.integrations.catalogUnavailable", "Integrations could not be loaded.")}</p>
            <Btn onClick={() => setCatalogRetry((value) => value + 1)}>{t("common.actions.retry", "Retry")}</Btn>
          </div>
        ) : providers.length === 0 ? (
          <div style={{ padding: 32, color: T.soft }}>{t("settings.integrations.noneAvailable", "No integrations are available for this tenant.")}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: `${T.space[12]} 0`, textAlign: "center", color: T.soft, fontSize: T.text.body.fontSize }}>{t("settings.integrations.noIntegrationsMatch", "No integrations match \"")}{" "}{query}"</div>
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: T.space[4] }}>
            {rows.map((p) => {
              const st = { kind: statusKind(p.id) };
              const isConn = st.kind === "connected";
              const btnBase = { flex: 1, height: 34, borderRadius: T.radius.md, fontSize: T.text.bodySm.fontSize, fontWeight: 600, cursor: "pointer", fontFamily: T.font };
              return (
                <Card key={p.id} style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer", borderTop: isConn ? `3px solid ${p.brand}` : "3px solid transparent" }} onClick={() => openDetail(p)}>
                  <div style={{ padding: `${T.space[4]} ${T.space[4]} ${T.space[4]}`, flex: 1, display: "flex", flexDirection: "column", gap: T.space[3] }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: T.space[3] }}>
                      <BrandIcon p={p} size={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1 }}>{p.name}</div>
                        <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1] }}>{p.category}</div>
                      </div>
                      {st.kind !== "disconnected" ? <StatusPill kind={st.kind} /> : null}
                    </div>
                    <p style={{ margin: 0, fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.5, flex: 1 }}>{p.desc.slice(0, 100)}{p.desc.length > 100 ? "…" : ""}</p>
                    <div style={{ display: "flex", gap: T.space[2], alignItems: "center" }}>
                      {canWrite ? (
                        isConn ? (
                          <Btn disabled={busyOp === p.id} onClick={(e) => { e.stopPropagation(); void doDisconnect(p); }} style={{ ...btnBase, border: `1px solid ${T.borderStrong}`, background: T.white, color: T.n2, justifyContent: "center", opacity: 1 }}>
                            {busyOp === p.id ? t("settings.settingsIntegrations.disconnecting", "Disconnecting…") : t("account.details.disconnect", "Disconnect")}
                          </Btn>
                        ) : st.kind === "configured-unverified" ? (
                          <Btn disabled={busyOp === p.id} onClick={(e) => { e.stopPropagation(); void doVerify(p); }} style={{ ...btnBase, border: `1px solid ${T.p1}`, background: T.p1, color: T.white, justifyContent: "center", opacity: 1 }}>
                            {busyOp === p.id ? t("settings.settingsIntegrations.verifying", "Verifying…") : t("settings.settingsIntegrations.verifyConnection", "Verify connection")}
                          </Btn>
                        ) : (
                          <Btn onClick={(e) => { e.stopPropagation(); openDetail(p); }} style={{ ...btnBase, border: `1px solid ${T.p1}`, background: T.p1, color: T.white, justifyContent: "center", opacity: 1 }}>
                            {t("settings.integrations.connect", "Connect")}</Btn>
                        )
                      ) : null}
                      <Btn onClick={(e) => { e.stopPropagation(); openDetail(p); }} style={{ height: 34, padding: `0 ${T.space[3]}`, borderRadius: T.radius.md, border: `1px solid ${T.borderStrong}`, background: T.white, color: T.n2, fontSize: T.text.bodySm.fontSize, fontWeight: 600, cursor: "pointer", fontFamily: T.font, opacity: 1 }}>{t("settings.integrations.details", "Details")}</Btn>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {/* ui-raw: jrni-ui Table can't express this row's conditional brand-color
                left border, the uppercase 11px header treatment, or the first-row-only
                divider without also picking up Table's built-in hover highlight — none
                of which exist in the original design, so a swap here isn't parity-safe. */}
            <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontFamily: T.font, fontSize: T.text.bodySm.fontSize }}>
              <thead>
                <tr style={{ background: T.n6 }}>
                  <th style={{ ...thStyle, width: "42%" }}>{t("settings.integrations.integration", "Integration")}</th>
                  <th style={{ ...thStyle, width: "15%" }}>{t("settings.integrations.category", "Category")}</th>
                  <th style={{ ...thStyle, width: "18%" }}>{t("settings.integrations.auth", "Auth")}</th>
                  <th style={{ ...thStyle, width: "13%" }}>{t("settings.integrations.status", "Status")}</th>
                  {canWrite ? <th style={{ ...thStyle, width: "12%", textAlign: "right" as const }}>{t("settings.integrations.action", "Action")}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => {
                  const st = { kind: statusKind(p.id) };
                  const isConn = st.kind === "connected";
                  return (
                    <tr key={p.id} onClick={() => openDetail(p)} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", borderLeft: isConn ? `3px solid ${p.brand}` : "3px solid transparent" }}>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
                          <BrandIcon p={p} size={32} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ color: T.n1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                            <div title={p.desc} style={{ color: T.soft, fontSize: T.text.label.fontSize, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.desc}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: T.n2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.category}</td>
                      <td style={{ ...tdStyle, color: T.soft, fontSize: T.text.label.fontSize, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.auth}</td>
                      <td style={tdStyle}><StatusPill kind={st.kind} /></td>
                      {canWrite ? (
                        <td style={{ ...tdStyle, textAlign: "right" as const }} onClick={(e) => e.stopPropagation()}>
                          {isConn ? (
                            <Btn size="sm" variant="secondary" disabled={busyOp === p.id} onClick={() => { void doDisconnect(p); }}>{busyOp === p.id ? t("settings.settingsIntegrations.disconnecting", "Disconnecting…") : t("account.details.disconnect", "Disconnect")}</Btn>
                          ) : st.kind === "configured-unverified" ? (
                            <Btn size="sm" variant="primary" disabled={busyOp === p.id} onClick={() => { void doVerify(p); }}>{busyOp === p.id ? t("settings.settingsIntegrations.verifying", "Verifying…") : t("settings.integrationDetail.verify", "Verify")}</Btn>
                          ) : (
                            <Btn size="sm" variant="primary" onClick={() => openDetail(p)}>{t("settings.integrations.connect", "Connect")}</Btn>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </SettingsShell>
  );
}
